import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { jornaisApi } from '@/services/jornais';

import styles from './PecasDoFato.module.css';

interface Props {
  fatoId: string;
  aoFechar: () => void;
}

/** As peças de um fato — a história inteira que a TV contou em pedaços.
 *
 *  A explosão do Umarizal foi sete laudas em 11/09/2026 e virou cinco matérias
 *  que se contradiziam no número de feridos. O operador precisa ver as peças
 *  juntas ANTES de escrever, e é isso que esta caixa mostra: cada peça com o
 *  seu jornal, a hora em que foi ao ar e o post que ela gerou.
 */
export function PecasDoFato({ fatoId, aoFechar }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fato', fatoId],
    queryFn: () => jornaisApi.fato(fatoId),
  });

  return (
    <div className={styles.fundo} onClick={aoFechar} role="presentation">
      <div
        className={styles.caixa}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Peças do fato"
      >
        <header className={styles.cabeca}>
          <div>
            <h2>{data?.titulo ?? 'Fato'}</h2>
            <p className={styles.fraco}>
              {data ? `${data.total} peça(s) desta história` : 'carregando…'}
            </p>
            {data && data.creditos && data.creditos.length > 0 && (
              <p className={styles.creditos}>
                Reportagem: {data.creditos.join(' · ')}
              </p>
            )}
          </div>
          <button type="button" onClick={aoFechar} className={styles.fechar}>
            fechar
          </button>
        </header>

        {isLoading && <p className={styles.fraco}>lendo as peças…</p>}
        {error && <p className={styles.erro}>Não foi possível abrir o fato.</p>}

        {data && (
          <ul className={styles.lista}>
            {data.pecas.map((p) => (
              <li key={p.lauda_id} className={styles.peca}>
                <div className={styles.pecaTitulo}>{p.titulo || '(sem retranca)'}</div>
                <div className={styles.pecaOrigem}>
                  {p.formato ? `${p.formato} · ` : ''}
                  {p.jornal}
                  {p.praca_nome ? ` · ${p.praca_nome}` : ''}
                  {p.ar ? ` · ar ${new Date(p.ar).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}` : ` · ${p.dia}`}
                  {` · ${p.chars} caracteres`}
                  {p.reporter ? ` · ${p.reporter}` : ''}
                </div>
                <div className={styles.pecaAcoes}>
                  <span className={styles.estado}>{p.estado}</span>
                  <Link to={`/jornais/${p.lauda_id}`}>esteira</Link>
                  {p.wp_id && p.site && (
                    <Link to={`/artigos/${p.wp_id}?site=${p.site}`}>o artigo</Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {data?.chave && <p className={styles.chave}>{data.chave}</p>}
      </div>
    </div>
  );
}
